// @ts-strict-ignore
import {
	AfterViewInit,
	Component,
	ComponentRef,
	inject,
	Injectable,
	input,
	output,
	signal,
	viewChild,
	ViewChild,
	ViewContainerRef,
} from "@angular/core";
import {CustomHeaderCalendarComponent} from "./custom-header-calendar/custom-header-calendar.component";
import {EventEmitterService} from "@event-emitter";
import {SimConfigurationService, SimEventService, SimMaterialDialogService, SimUserExtendedService, SimUtilityService} from "@services";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import {EVENT_FILTER_OPTIONS} from "../../../../modules/dialog/material-dialogs/event-dialogs/event-filter-dialog/event-filter-dialog.component";
import {dateHelperEventFromCalendar_containsDate, Event, ExternalCalendarReference, getColorForEvent, Permissions} from "@models";
import {EVENT_ACTION_BUTTONS, EventSelectionComponent} from "../event-selection/event-selection.component";
import {NativeDateAdapter} from "@angular/material/core";
import {Translations} from "@translations";

import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, {DateClickArg} from "@fullcalendar/interaction";
import deLocale from "@fullcalendar/core/locales/de";
import enLocale from "@fullcalendar/core/locales/en-gb";
import frLocale from "@fullcalendar/core/locales/fr";
import ruLocale from "@fullcalendar/core/locales/ru";
import {Router} from "@angular/router";
// TODO(SIMSOFT-11935): ANGULAR_ANIMATIONS_CLEANUP
import {fadeInOnEnterAnimation, slideInRightOnEnterAnimation, zoomInOnEnterAnimation} from "angular-animations";
import {Calendar, CalendarOptions, DatesSetArg, EventApi, EventClickArg, EventHoveringArg, EventInput} from "@fullcalendar/core";
import {FullCalendarComponent, FullCalendarModule} from "@fullcalendar/angular";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {SegmentedButtonComponent} from "@common/ui/segmented-button/segmented-button.component";
import {MatTooltip} from "@angular/material/tooltip";
import {ComponentLoadingContainerComponent} from "../component-loading-container/component-loading-container.component";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {ButtonSegmentComponent} from "@common/ui/segmented-button/button-segment/button-segment.component";
import {EventCalendarTooltipComponent} from "./event-calendar-tooltip/event-calendar-tooltip.component";
import {CalendarSortingComponent, CalendarSortOrder, CalendarSortValue} from "./calendar-sorting/calendar-sorting.component";
import {EventUtils} from "./utils/event.utils";

dayjs.extend(weekOfYear);

const CALENDAR_VIEW = 0;
const EVENT_LIST_VIEW = 1;

// SIMSOFT-7420: see https://github.com/angular/components/issues/16458
@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
	constructor(private _translate: TranslateService) {
		super(_translate.currentLang());

		_translate.onLangChange.subscribe((e) => {
			console.log(`New Lang: ${e.lang}`);

			this.setLocale(e.lang);
		});
	}

	getFirstDayOfWeek() {
		return SimConfigurationService.settings.weekStartDay;
	}
}

@UntilDestroy()
@Component({
	selector: "sim-event-calendar",
	templateUrl: "./event-calendar.component.html",
	styleUrls: ["./event-calendar.component.scss"],
	providers: [EventEmitterService],
	animations: [
		zoomInOnEnterAnimation({duration: 175}),
		fadeInOnEnterAnimation({duration: 175}),
		slideInRightOnEnterAnimation({duration: 175}),
	],
	imports: [
		ButtonSegmentComponent,
		MatTooltip,
		ComponentLoadingContainerComponent,
		MatIconButton,
		MatIcon,
		MatButton,
		FullCalendarModule,
		EventSelectionComponent,
		TranslatePipe,
		SegmentedButtonComponent,
		CalendarSortingComponent,
	],
})
export class EventCalendarComponent implements AfterViewInit {
	readonly T = Translations;

	@ViewChild("calendar") private readonly calendarRef?: FullCalendarComponent;

	protected readonly calViewToggler = viewChild<SegmentedButtonComponent>("calViewToggler");

	public readonly onEventSelected = output<Event>();
	public showHeader = input<boolean>(true);
	public showSelectAll = input<boolean>(false);
	public eventActions = input<EVENT_ACTION_BUTTONS>(null);
	public showEventsForEventManager = input<boolean>(false);

	readonly #viewContainerRef = inject(ViewContainerRef);

	public isCalView: boolean = true;
	public loadingListedEvents: boolean = false;
	#tooltipRef: ComponentRef<EventCalendarTooltipComponent> | null = null;

	public filterOptions: EVENT_FILTER_OPTIONS = null;
	protected currentSortValue = signal<CalendarSortValue>("dateTime");
	#currentSortOrder: CalendarSortOrder = "asc";
	public readonly calendarOptions: CalendarOptions = {
		locales: [enLocale, deLocale, frLocale, ruLocale],
		locale: this.translate.currentLang(),
		firstDay: SimConfigurationService.settings.weekStartDay,
		eventClick: (info) => {
			this.#hideTooltip();
			this.eventClicked(info);
		},
		eventMouseEnter: (ev: EventHoveringArg) => this.#showTooltip(ev),
		eventMouseLeave: () => this.#hideTooltip(),
		dateClick: this.dateClick.bind(this),
		displayEventTime: false,
		headerToolbar: false,
		height: "100%",
		plugins: [dayGridPlugin, interactionPlugin],
		weekends: true,
		eventMaxStack: 3,
		dayMaxEvents: 4,
		eventOrder: this.eventComparator.bind(this),
	};

	constructor(
		private _simEventService: SimEventService,
		private translate: TranslateService,
		private _eventEmitterService: EventEmitterService,
		private _router: Router,
		private _simUserService: SimUserExtendedService,
		private _dialogService: SimMaterialDialogService,
	) {
		CustomHeaderCalendarComponent.activeDateChanged.pipe(untilDestroyed(this)).subscribe(async (date: Date) => {
			this.calendarApi?.gotoDate(date);
		});

		this._eventEmitterService.onEventsChanged.subscribe(() => {
			this.calendarApi?.gotoDate(this.calendarApi?.getCurrentData().currentDate);
		});

		this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe(({lang}) => {
			this.calendarApi?.setOption("locale", lang);
		});
	}

	private get calendarApi(): Calendar {
		return this.calendarRef?.getApi();
	}

	ngAfterViewInit(): void {
		this.initCalendarEvents();
	}

	private initCalendarEvents() {
		if (!this.calendarApi) {
			console.error("Calendar not initialized");
			return;
		}
		this.calendarApi?.on("datesSet", async (args) => {
			try {
				this.loadingListedEvents = true;
				this.calendarApi?.removeAllEventSources();

				const internalPromise = this.getInternalEvents(args).then((events) => {
					this.calendarApi?.getEventSourceById("internal")?.remove();
					this.calendarApi?.addEventSource({id: "internal", events: events});
				});

				//At least wait a small delay so the loading doesn't seem flickery
				await Promise.all([internalPromise, SimUtilityService.delay(250)]);
			} catch (e) {
				console.error(e);
			} finally {
				this.loadingListedEvents = false;
			}
		});

		//we have to tell the calendar to set the date after the datesSet logic is initialized
		this.calendarApi?.today();
	}

	private dateSelected(date: Date) {
		const from = SimUtilityService.getStartofDay(date);
		const to = SimUtilityService.getEndofDay(date);

		this.filterOptions = {
			selectedDateRange: {
				start: from?.toDate(),
				end: to?.toDate(),
			},
		};

		this.calViewToggler()?.setActiveSegment(EVENT_LIST_VIEW);
	}

	public backButtonFromEventSelectionClicked() {
		this.calViewToggler()?.setActiveSegment(CALENDAR_VIEW);
	}

	protected setCalendarView() {
		if (!this.isCalView) {
			//as the calendar was destroyed, we have to reinit it
			setTimeout(() => this.initCalendarEvents(), 100);
		}
		this.isCalView = true;
	}

	protected setEventListView() {
		this.isCalView = false;
	}

	private async getInternalEvents(arg: DatesSetArg): Promise<EventInput[]> {
		//we don't want the full range of events, just the ones that are in the current view
		const fromDate = SimUtilityService.getStartofMonth(arg.view.calendar.getDate());
		const toDate = SimUtilityService.getEndofMonth(arg.view.calendar.getDate());

		let events = this.showEventsForEventManager()
			? await this._simEventService.listEvents({
					searchTermName: null,
					searchTermDescription: null,
					page: 0,
					pageSize: 1000,
					from: fromDate.toDate(),
					filterEventStates: null,
					sortColumn: null,
					ownerId: null,
					to: toDate.toDate(),
					scenarioId: null,
					sortAscending: false,
					showFavoritesOnly: false,
				})
			: await this._simEventService.listEventsForScenarioRecordingSelection(
					null,
					null,
					null,
					false,
					fromDate.toDate(),
					toDate.toDate(),
					null,
					null,
					null,
					null,
					null,
					false,
					false,
					0,
					1000,
				);

		return this.mapEventsToCalendarEvents(events?.data);
	}

	private mapEventsToCalendarEvents(events: Event[]): EventInput[] {
		if (!events) {
			return [];
		}

		return events.map((event) => {
			return <EventInput>{
				id: event.id,
				start: event.start,
				end: event.end,
				title: event.name,
				backgroundColor: getColorForEvent(event),
				borderColor: getColorForEvent(event),
				extendedProps: {
					event,
				},
			};
		});
	}

	public async eventFromEventSelectionSelected(event: Event) {
		await this._router.navigate(["/event-detail", event.id]);
	}

	public getCurrentCalendarDate(): Date {
		return this.calendarApi?.getCurrentData()?.currentDate;
	}

	public changeMonth(direction: 1 | -1) {
		const newDate = this.getCurrentCalendarDate();
		newDate.setDate(1); // Set to first day to avoid overflow on month end
		newDate.setMonth(newDate.getMonth() + direction);
		this.calendarApi?.gotoDate(newDate);
	}

	public today() {
		this.calendarApi?.today();
	}

	public getTranslationOfCalendarMonth(month: number) {
		const map = {
			0: Translations.CALENDAR_MONTH_JANNUARY,
			1: Translations.CALENDAR_MONTH_FEBRUARY,
			2: Translations.CALENDAR_MONTH_MARCH,
			3: Translations.CALENDAR_MONTH_APRIL,
			4: Translations.CALENDAR_MONTH_MAY,
			5: Translations.CALENDAR_MONTH_JUNE,
			6: Translations.CALENDAR_MONTH_JULY,
			7: Translations.CALENDAR_MONTH_AUGUST,
			8: Translations.CALENDAR_MONTH_SEPTEMBER,
			9: Translations.CALENDAR_MONTH_OCTOBER,
			10: Translations.CALENDAR_MONTH_NOVEMBER,
			11: Translations.CALENDAR_MONTH_DECEMBER,
		};

		const key = map[month];
		if (key) {
			return this.translate.instant(key);
		}
	}

	private async eventClicked(arg: EventClickArg) {
		const event = arg.event;

		const externalRef = event.extendedProps["externalRef"] as ExternalCalendarReference;
		if (externalRef?.temporary) {
			if (!this._simUserService.hasPermission(Permissions.EventPlanner_Create)) {
				return;
			}
			await this._dialogService
				.openCreateDraftEventDialog({
					preselectedDate: null,
				})
				.then();
			return;
		}

		const commands = this.showEventsForEventManager() ? ["/web-manager/list/event-manager/" + event.id] : ["/event-detail/" + event.id];
		await this._router.navigate(commands);
	}

	private dateClick(arg: DateClickArg) {
		if (this.showEventsForEventManager()) {
			if (!this._simUserService.hasPermission(Permissions.EventPlanner_Create)) {
				return;
			}
			this._dialogService
				.openCreateDraftEventDialog({
					preselectedDate: arg.date,
				})
				.then();
		} else if (
			this.calendarApi
				?.getEvents()
				.some((event) => event.source?.id == "internal" && dateHelperEventFromCalendar_containsDate(event, arg.date))
		) {
			this.dateSelected(arg.date);
		}
	}

	public onSortChanged(value: CalendarSortValue): void {
		this.currentSortValue.set(value);
		this.calendarApi?.setOption("eventOrder", this.eventComparator.bind(this));
	}

	public onSortOrderChanged(order: CalendarSortOrder): void {
		this.#currentSortOrder = order;
		this.calendarApi?.setOption("eventOrder", this.eventComparator.bind(this));
	}

	/**
	 * Custom comparator that:
	 * 1) Puts events from source "internal" before any other source.
	 * 2) For events from the same source, sorts by name or start time based on currentSortValue.
	 */
	private eventComparator(a: EventApi, b: EventApi): number {
		const prioritySource = "internal";
		const sourceA = a.source?.id;
		const sourceB = b.source?.id;

		if (sourceA !== sourceB) {
			if (sourceA === prioritySource) {
				return -1;
			}
			if (sourceB === prioritySource) {
				return 1;
			}
			return 0;
		}

		return EventUtils.compareEvents(
			a.extendedProps["event"] as Event,
			b.extendedProps["event"] as Event,
			this.currentSortValue(),
			this.#currentSortOrder,
		);
	}

	#showTooltip(info: EventHoveringArg): void {
		this.#hideTooltip();
		this.#tooltipRef = this.#viewContainerRef.createComponent(EventCalendarTooltipComponent);

		this.#tooltipRef.setInput("title", info.event.title);
		this.#tooltipRef.setInput("targetElement", info.el);

		const event = info.event.extendedProps["event"] as Event;
		const firstSessionStart = event?.firstSessionStart;
		const lastSessionEnd = event?.lastSessionEnd;

		// if at least a session start is defined, we show it
		let range: {start: Date | string; end?: Date | string} = info.event._instance.range;
		if (firstSessionStart) {
			range = {
				start: firstSessionStart,
				end: lastSessionEnd,
			};
		}

		this.#tooltipRef.setInput("date", range);

		document.body.appendChild(this.#tooltipRef.location.nativeElement);
	}

	#hideTooltip(): void {
		if (this.#tooltipRef) {
			this.#tooltipRef.destroy();
			this.#tooltipRef = null;
		}
	}
}
